import { Button, message, Modal, Spin } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useAuth0 } from "services/auth/authService";
import ExcelDownloadService from "services/excel/excelDownloadService";
import logger from "services/logger/logger";
import { SurveyModel } from "survey-core";
import "survey-core/defaultV2.css";
import { Survey } from "survey-react-ui";
import EmailVerification from "../../components/EmailVerification/EmailVerification";
import ExcelDataExtractor from "../../components/ExcelDataExtractor";
import ExcelReuploadModal from "../../components/ExcelReuploadModal/ExcelReuploadModal";
import RestrictedSmartBEA from "../../components/RestrictedAccess/RestrictedSmartBEA";
import SurveyResponseProcessor from "../../components/SurveyResponseProcessor/SurveyResponseProcessor";
import { surveyThemeConfig } from "../../config/survey-theme-config";
import useRoleAndPermission from "../../hooks/use-role-and-permission";
import useUserSmartBEAStatus from "../../hooks/useUserSmartBEAStatus";
import "../../styles/survey-theme.scss";
import { getSurveyDataByEmail, saveSurveyData } from "../../utils/indexedDB";
import { json } from "./default_survey";
import "./SmartBEA.scss";

const STORAGE_KEY = "smartBEA_draftData";

const SmartBEA = () => {
  const location = useLocation();
  const history = useHistory();
  const { loginWithRedirect, isAuthenticated, user, isLoading: authLoading } = useAuth0();
  const { isRestricted, loading: smartBEAStatusLoading } = useUserSmartBEAStatus();
  const roleAndPermission = useRoleAndPermission();
  const [survey, setSurvey] = useState<SurveyModel | null>(null);
  const [showExcelExtractor, setShowExcelExtractor] = useState(false);
  const [showSurveyProcessor, setShowSurveyProcessor] = useState(false);
  const [surveyData, setSurveyData] = useState<any>(null);
  const [combinedData, setCombinedData] = useState<any>(null);
  const [showReuploadModal, setShowReuploadModal] = useState(false);
  const [excelError, setExcelError] = useState<string | null>(null);
  const [showRestoreDataModal, setShowRestoreDataModal] = useState(false);
  const [storedData, setStoredData] = useState<any>(null);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [userEmail, setUserEmail] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Declare checkStoredData function before using it
  const checkStoredData = () => {
    // Skip this check if we're loading auth or showing email verification
    if (authLoading || showEmailVerification) {
      return;
    }

    // If we should skip the survey form, check for data in IndexedDB
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    if (shouldSkipSurveyForm() && isAuthenticated && user?.email) {
      setIsLoading(true);
      getSurveyDataByEmail(user.email)
        .then((data) => {
          if (data && data.surveyData) {
            // Check if there's Excel data that needs processing first
            if (
              data.surveyData.location_consumption &&
              Array.isArray(data.surveyData.location_consumption) &&
              data.surveyData.location_consumption.length > 0
            ) {
              // If there's Excel data, show the Excel extractor first
              setSurveyData(data.surveyData);
              setShowExcelExtractor(true);
              setShowSurveyProcessor(false);
            } else {
              // Found data in IndexedDB, go straight to processor
              setSurveyData(data.surveyData);
              setShowSurveyProcessor(true);
              setShowExcelExtractor(false);
            }
          } else {
            // No data found, initialize survey
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            initializeSurvey();
          }
          setIsLoading(false);
        })
        .catch((error) => {
          logger.error("Error checking for stored survey data", error);
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          initializeSurvey();
          setIsLoading(false);
        });
      return;
    }

    // Check if there's data in localStorage
    const storedSurveyData = localStorage.getItem(STORAGE_KEY);

    if (storedSurveyData) {
      try {
        const parsedData = JSON.parse(storedSurveyData);

        setStoredData(parsedData);
        setShowRestoreDataModal(true);
        return;
      } catch (error) {
        logger.error("Error parsing stored survey data", error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }

    // If we're authenticated, check if we have data in IndexedDB
    if (isAuthenticated && user?.email) {
      getSurveyDataByEmail(user.email)
        .then((data) => {
          if (data && data.surveyData) {
            // Check if there's Excel data that needs processing first
            if (
              data.surveyData.location_consumption &&
              Array.isArray(data.surveyData.location_consumption) &&
              data.surveyData.location_consumption.length > 0
            ) {
              // If there's Excel data, show the Excel extractor first
              setSurveyData(data.surveyData);
              setShowExcelExtractor(true);
              setShowSurveyProcessor(false);
            } else {
              // Found data in IndexedDB, go straight to processor
              setSurveyData(data.surveyData);
              setShowSurveyProcessor(true);
              setShowExcelExtractor(false);
            }
          } else {
            // If no stored data, initialize the survey
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            initializeSurvey();
          }
        })
        .catch((error) => {
          logger.error("Error checking for stored survey data", error);
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          initializeSurvey();
        });
    } else {
      // If not authenticated, just initialize the survey
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      initializeSurvey();
    }
  };

  // Check if the component is being accessed from a public route
  const isPublicRoute = useCallback((): boolean => {
    // The public route path includes '/public/smartbea'
    return location.pathname.includes("/public/smartbea");
  }, [location.pathname]);

  // Check if we should skip the survey form and go directly to processor
  const shouldSkipSurveyForm = useCallback((): boolean => {
    return location.state && (location.state as any).skipSurveyForm === true;
  }, [location.state]);

  // Check for stored data in localStorage and IndexedDB when component mounts
  useEffect(() => {
    // If user is already onboarded and has locations, prevent access to SmartBEA in non-public routes
    if (isRestricted && !isPublicRoute()) {
      // Show a message before redirecting
      message.info("You already have access to SmartBEA through your locations");
      // Redirect to locations after a short delay
      setIsRedirecting(true);
      const timer = setTimeout(() => {
        history.push("/locations");
      }, 2000);
      return () => clearTimeout(timer);
    }

    // Check for email verification error in URL
    const searchParams = new URLSearchParams(location.search);
    const urlError = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    // If there's an unauthorized or access_denied error with email verification message
    if (
      (urlError === "unauthorized" || urlError === "access_denied") &&
      errorDescription?.toLowerCase().includes("verify your email")
    ) {
      // Extract email from localStorage if available
      const storedEmail = localStorage.getItem("lastUsedEmail");
      if (storedEmail) {
        setUserEmail(storedEmail);
        setShowEmailVerification(true);

        // Store flag indicating verification is in progress
        localStorage.setItem("emailVerificationInProgress", "true");
        return; // Don't initialize survey if showing email verification
      }
    } else if (isAuthenticated && localStorage.getItem("emailVerificationInProgress") === "true") {
      // If user is now authenticated and there was a verification in progress
      const storedEmail = localStorage.getItem("lastUsedEmail");
      if (storedEmail) {
        getSurveyDataByEmail(storedEmail)
          .then((data) => {
            if (data && data.surveyData) {
              // Clear verification flags
              localStorage.removeItem("emailVerificationInProgress");

              // Set the survey data
              const surveyDataWithExcel = data.surveyData;

              // Set the survey data and show Excel extractor, explicitly hide survey processor
              setSurveyData(surveyDataWithExcel);
              setShowExcelExtractor(true);
              setShowSurveyProcessor(false);
            }
          })
          .catch((error) => {
            logger.error("Error retrieving survey data after authentication:", error);
            localStorage.removeItem("emailVerificationInProgress");
          });
      }
    }

    // Only run this effect if Auth0 has finished loading and we haven't detected a restricted user
    if (!authLoading && !isRedirecting) {
      checkStoredData();
    }

    const timer = setTimeout(() => {
      if (!isRedirecting) {
        checkStoredData();
      }
    }, 1000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, location.state, authLoading, location.search, isRestricted, isPublicRoute]);

  // Handle email verification return
  useEffect(() => {
    if (isAuthenticated) {
      const verificationCompleted = localStorage.getItem("emailVerificationCompleted");
      if (verificationCompleted === "true") {
        // Retrieve the user's survey data
        const storedEmail = localStorage.getItem("lastUsedEmail");
        if (storedEmail) {
          getSurveyDataByEmail(storedEmail)
            .then((data) => {
              if (data && data.surveyData) {
                // Clear verification flags
                localStorage.removeItem("emailVerificationCompleted");
                localStorage.removeItem("emailVerificationInProgress");

                // Set the survey data
                const surveyDataWithExcel = data.surveyData;

                // Set the survey data and show Excel extractor
                setSurveyData(surveyDataWithExcel);
                setShowExcelExtractor(true);
                setShowSurveyProcessor(false);
              }
            })
            .catch((error) => {
              logger.error("Error retrieving survey data after verification completed:", error);
              localStorage.removeItem("emailVerificationCompleted");
              localStorage.removeItem("emailVerificationInProgress");
            });
        }
      }
    }
  }, [isAuthenticated]);

  // Initialize the survey with default configuration
  const initializeSurvey = useCallback(() => {
    const surveyModel = new SurveyModel(json);

    // Apply theme configuration
    if (typeof surveyModel.applyTheme === "function") {
      surveyModel.applyTheme(surveyThemeConfig as any);
    } else {
      // For older versions of the survey library that don't have applyTheme
      // Apply theme properties directly
      Object.assign(surveyModel, {
        questionTitleLocation: "top",
        showQuestionNumbers: "off",
        showProgressBar: "bottom",
        ...surveyThemeConfig,
      });
    }

    // Set up survey completion handler
    surveyModel.onComplete.add((sender) => {
      const { data } = sender;
      setSurveyData(data);
      setShowExcelExtractor(true);

      // Save draft data to localStorage with better error handling
      try {
        const storageData = {
          data,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
      } catch (error) {
        logger.error("Failed to save survey data to localStorage", error);
        // Try with a smaller subset of data if the original is too large
        try {
          const essentialData = { ...data };
          // Remove any potentially large fields that aren't essential
          delete essentialData.location_consumption;
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
              data: essentialData,
              timestamp: new Date().toISOString(),
            })
          );
        } catch (fallbackError) {
          logger.error("Failed to save even reduced survey data", fallbackError);
        }
      }
    });

    // Save data as user types
    if (surveyModel.onValueChanged && typeof surveyModel.onValueChanged.add === "function") {
      surveyModel.onValueChanged.add((sender) => {
        const data = sender.data || {};
        const total = Number(data.total_guest_rooms) || 0;
        const studios = Number(data.no_of_studios) || 0;
        const oneBeds = Number(data.no_of_1_bedrooms) || 0;
        const twoBeds = Number(data.no_of_2_bedrooms) || 0;
        const threeBeds = Number(data.no_of_3_bedrooms) || 0;
        const sum = studios + oneBeds + twoBeds + threeBeds;

        // Only validate if all fields are filled
        const allFilled =
          data.total_guest_rooms !== undefined &&
          data.no_of_studios !== undefined &&
          data.no_of_1_bedrooms !== undefined &&
          data.no_of_2_bedrooms !== undefined &&
          data.no_of_3_bedrooms !== undefined;

        // Clear previous errors
        ["total_guest_rooms", "no_of_studios", "no_of_1_bedrooms", "no_of_2_bedrooms", "no_of_3_bedrooms"].forEach(
          (name) => {
            const q = sender.getQuestionByName(name);
            if (q) q.clearErrors();
          }
        );

        if (allFilled && sum !== total) {
          const errorMsg =
            "The sum of all room types (studios, 1-bed, 2-bed, 3-bed) must equal the total number of guest rooms.";
          ["total_guest_rooms", "no_of_studios", "no_of_1_bedrooms", "no_of_2_bedrooms", "no_of_3_bedrooms"].forEach(
            (name) => {
              const q = sender.getQuestionByName(name);
              if (q) q.addError(errorMsg);
            }
          );
        }

        // --- Floor percentage validation ---
        const floorFields = [
          "rooms_floor_percentage",
          "common_areas_floor_percentage",
          "restaurants_floor_percentage",
          "spas_floor_percentage",
          "back_offices_floor_percentage",
          "other_areas_floor_percentage",
        ];
        const floorValues = floorFields.map((f) => Number(data[f]));
        const allFloorFilled = floorValues.every((v) => !Number.isNaN(v));
        const floorSum = floorValues.reduce((a, b) => a + b, 0);
        // Clear previous errors
        floorFields.forEach((name) => {
          const q = sender.getQuestionByName(name);
          if (q) q.clearErrors();
        });
        if (allFloorFilled && floorSum !== 100) {
          const errorMsg = "The sum of all floor percentage fields must equal 100.";
          floorFields.forEach((name) => {
            const q = sender.getQuestionByName(name);
            if (q) q.addError(errorMsg);
          });
        }
        // --- End floor percentage validation ---

        // Existing localStorage save logic
        const currentData = sender.data;
        try {
          if (currentData && Object.keys(currentData).length > 0) {
            localStorage.setItem(
              STORAGE_KEY,
              JSON.stringify({
                data: currentData,
                timestamp: new Date().toISOString(),
              })
            );
          }
        } catch (error) {
          logger.error("Error saving form data to localStorage:", error);
        }
      });
    }

    // Add server-side validation to block navigation if sum does not match
    if (surveyModel.onServerValidateQuestions && typeof surveyModel.onServerValidateQuestions.add === "function") {
      surveyModel.onServerValidateQuestions.add((sender, options) => {
        const data = sender.data || {};
        const total = Number(data.total_guest_rooms) || 0;
        const studios = Number(data.no_of_studios) || 0;
        const oneBeds = Number(data.no_of_1_bedrooms) || 0;
        const twoBeds = Number(data.no_of_2_bedrooms) || 0;
        const threeBeds = Number(data.no_of_3_bedrooms) || 0;
        const sum = studios + oneBeds + twoBeds + threeBeds;

        const allFilled =
          data.total_guest_rooms !== undefined &&
          data.no_of_studios !== undefined &&
          data.no_of_1_bedrooms !== undefined &&
          data.no_of_2_bedrooms !== undefined &&
          data.no_of_3_bedrooms !== undefined;

        // Use a new errors object to avoid direct assignment to function parameter
        const newErrors = options.errors ? { ...options.errors } : {};

        if (allFilled && sum !== total) {
          const errorMsg =
            "The sum of all room types (studios, 1-bed, 2-bed, 3-bed) must equal the total number of guest rooms.";
          ["total_guest_rooms", "no_of_studios", "no_of_1_bedrooms", "no_of_2_bedrooms", "no_of_3_bedrooms"].forEach(
            (name) => {
              newErrors[name] = errorMsg;
            }
          );
        }

        // --- Floor percentage validation ---
        const floorFields = [
          "rooms_floor_percentage",
          "common_areas_floor_percentage",
          "restaurants_floor_percentage",
          "spas_floor_percentage",
          "back_offices_floor_percentage",
          "other_areas_floor_percentage",
        ];
        const floorValues = floorFields.map((f) => Number(data[f]));
        const allFloorFilled = floorValues.every((v) => !Number.isNaN(v));
        const floorSum = floorValues.reduce((a, b) => a + b, 0);
        if (allFloorFilled && floorSum !== 100) {
          const errorMsg = "The sum of all floor percentage fields must equal 100.";
          floorFields.forEach((name) => {
            newErrors[name] = errorMsg;
          });
        }
        // --- End floor percentage validation ---

        // eslint-disable-next-line no-param-reassign
        options.errors = newErrors;
        options.complete();
      });
    }

    // Add handler to clear custom errors when navigating between pages
    if (surveyModel.onCurrentPageChanged && typeof surveyModel.onCurrentPageChanged.add === "function") {
      surveyModel.onCurrentPageChanged.add((sender) => {
        ["total_guest_rooms", "no_of_studios", "no_of_1_bedrooms", "no_of_2_bedrooms", "no_of_3_bedrooms"].forEach(
          (name) => {
            const q = sender.getQuestionByName(name);
            if (q) q.clearErrors();
          }
        );
      });
    }

    setSurvey(surveyModel);
  }, []);

  // Set up global Excel download function
  useMemo(() => {
    const downloadProcessedExcel = async () => {
      try {
        const s3Url =
          "https://s3.ap-southeast-1.amazonaws.com/storage-prod.sensorflow.cloud/smartbea/SmartBEA+Data+Collection+-+v2.4+(no+logo).xlsx";
        await ExcelDownloadService.downloadAndProcessExcelFile(s3Url);
      } catch (error) {
        message.error("Failed to download Excel file. Please try again.");
      }
    };

    // Add the function to the window object so it can be called from the HTML
    (window as any).downloadProcessedExcel = downloadProcessedExcel;

    // Cleanup function to remove the global function when component unmounts
    return () => {
      delete (window as any).downloadProcessedExcel;
    };
  }, []);

  // Handle public route submissions with IndexedDB storage
  const handlePublicRouteSubmission = useCallback(async (data: any) => {
    try {
      setIsLoading(true);

      // Extract email from the survey data
      const email = data.email || "";
      if (!email) {
        message.error("Email address is required");
        setIsLoading(false);
        return;
      }

      // Store the email for later use
      setUserEmail(email);

      // Also store in localStorage for email verification flow
      localStorage.setItem("lastUsedEmail", email);

      // Prepare data for IndexedDB storage
      const publicSubmissionData = {
        email,
        surveyData: data,
        timestamp: new Date().toISOString(),
      };

      // Save to IndexedDB instead of localStorage
      await saveSurveyData(publicSubmissionData);

      // Store survey data in component state
      setSurveyData(data);

      // For public routes, we need to handle Auth0 differently
      // Instead of using loginWithRedirect which requires Auth0Provider,
      // we'll redirect to Auth0's universal login page directly
      const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;
      const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;

      if (!auth0Domain || !clientId) {
        message.error("Authentication configuration is missing. Please contact support.");
        setIsLoading(false);
        return;
      }

      // Construct Auth0 universal login URL with proper parameters
      const returnTo = encodeURIComponent(`${window.location.origin}/callback`);
      const auth0LoginUrl =
        `https://${auth0Domain}/authorize?` +
        `client_id=${clientId}` +
        `&response_type=code` +
        `&redirect_uri=${returnTo}` +
        `&scope=openid%20email%20profile` +
        `&login_hint=${encodeURIComponent(email)}` +
        `&screen_hint=signup` +
        `&state=${encodeURIComponent(JSON.stringify({ returnTo: window.location.pathname, email }))}` +
        `&prompt=login`;

      // Redirect to Auth0's universal login
      window.location.href = auth0LoginUrl;

      setIsLoading(false);
    } catch (error) {
      logger.error("Error saving survey data for public submission:", error);
      message.error("There was an error saving your information. Please try again.");
      setIsLoading(false);
    }
  }, []);

  // Function to restore data from localStorage
  const handleRestoreData = () => {
    if (storedData && survey) {
      try {
        // The stored data contains {data, timestamp}, so we need to use storedData.data
        survey.data = storedData.data;
        setShowRestoreDataModal(false);
        message.success("Your previous data has been restored");
      } catch (error) {
        message.error("Unable to restore previous data");
      }
    } else {
      message.warning("No previous data available to restore");
      setShowRestoreDataModal(false);
    }
  };

  // Function to discard stored data and start fresh
  const handleDiscardStoredData = () => {
    localStorage.removeItem(STORAGE_KEY);
    setShowRestoreDataModal(false);
    message.info("Starting with a fresh form");
  };

  useEffect(() => {
    // Don't initialize survey if showing email verification
    if (showEmailVerification) {
      return;
    }

    // Initialize the survey when the component mounts
    const newSurvey = new SurveyModel(json);
    // Apply theme with type casting to avoid TypeScript error
    if (typeof newSurvey.applyTheme === "function") {
      newSurvey.applyTheme(surveyThemeConfig as any);
    } else {
      // For older versions of the survey library that don't have applyTheme
      // Apply theme properties directly
      Object.assign(newSurvey, {
        questionTitleLocation: "top",
        showQuestionNumbers: "off",
        showProgressBar: "bottom",
        ...surveyThemeConfig,
      });
    }
    newSurvey.onComplete.add((sender) => {
      const { data } = sender;

      // Store email for potential verification needs
      if (data.email) {
        localStorage.setItem("lastUsedEmail", data.email);
      }

      // For public routes, handle submission differently
      if (isPublicRoute()) {
        handlePublicRouteSubmission(data);
      } else {
        setSurveyData(data);
        setShowExcelExtractor(true);
      }
    });

    // Save data as user types
    if (newSurvey.onValueChanged && typeof newSurvey.onValueChanged.add === "function") {
      newSurvey.onValueChanged.add((sender) => {
        const data = sender.data || {};
        const total = Number(data.total_guest_rooms) || 0;
        const studios = Number(data.no_of_studios) || 0;
        const oneBeds = Number(data.no_of_1_bedrooms) || 0;
        const twoBeds = Number(data.no_of_2_bedrooms) || 0;
        const threeBeds = Number(data.no_of_3_bedrooms) || 0;
        const sum = studios + oneBeds + twoBeds + threeBeds;

        // Only validate if all fields are filled
        const allFilled =
          data.total_guest_rooms !== undefined &&
          data.no_of_studios !== undefined &&
          data.no_of_1_bedrooms !== undefined &&
          data.no_of_2_bedrooms !== undefined &&
          data.no_of_3_bedrooms !== undefined;

        // Clear previous errors
        ["total_guest_rooms", "no_of_studios", "no_of_1_bedrooms", "no_of_2_bedrooms", "no_of_3_bedrooms"].forEach(
          (name) => {
            const q = sender.getQuestionByName(name);
            if (q) q.clearErrors();
          }
        );

        if (allFilled && sum !== total) {
          const errorMsg =
            "The sum of all room types (studios, 1-bed, 2-bed, 3-bed) must equal the total number of guest rooms.";
          ["total_guest_rooms", "no_of_studios", "no_of_1_bedrooms", "no_of_2_bedrooms", "no_of_3_bedrooms"].forEach(
            (name) => {
              const q = sender.getQuestionByName(name);
              if (q) q.addError(errorMsg);
            }
          );
        }

        // --- Floor percentage validation ---
        const floorFields = [
          "rooms_floor_percentage",
          "common_areas_floor_percentage",
          "restaurants_floor_percentage",
          "spas_floor_percentage",
          "back_offices_floor_percentage",
          "other_areas_floor_percentage",
        ];
        const floorValues = floorFields.map((f) => Number(data[f]));
        const allFloorFilled = floorValues.every((v) => !Number.isNaN(v));
        const floorSum = floorValues.reduce((a, b) => a + b, 0);
        // Clear previous errors
        floorFields.forEach((name) => {
          const q = sender.getQuestionByName(name);
          if (q) q.clearErrors();
        });
        if (allFloorFilled && floorSum !== 100) {
          const errorMsg = "The sum of all floor percentage fields must equal 100.";
          floorFields.forEach((name) => {
            const q = sender.getQuestionByName(name);
            if (q) q.addError(errorMsg);
          });
        }
        // --- End floor percentage validation ---

        // Existing localStorage save logic
        const currentData = sender.data;
        try {
          if (currentData && Object.keys(currentData).length > 0) {
            localStorage.setItem(
              STORAGE_KEY,
              JSON.stringify({
                data: currentData,
                timestamp: new Date().toISOString(),
              })
            );
          }
        } catch (error) {
          logger.error("Error saving form data to localStorage:", error);
        }
      });
    }

    // Check if we have any stored data we can restore
    try {
      const localStorageData = localStorage.getItem(STORAGE_KEY);
      if (localStorageData) {
        const parsedLocalData = JSON.parse(localStorageData);
        // Validate the stored data before trying to restore it
        if (parsedLocalData && parsedLocalData.data && Object.keys(parsedLocalData.data).length > 0) {
          // If the stored data is valid and the modal isn't being shown,
          // we can apply it directly to the new survey
          if (!showRestoreDataModal) {
            try {
              newSurvey.data = parsedLocalData.data;
            } catch (applyError) {
              logger.error("Failed to apply stored data to survey", applyError);
            }
          }
        }
      }
    } catch (error) {
      logger.error("Error checking localStorage for survey data", error);
    }

    // Set survey width to match container width
    newSurvey.width = "100%";
    // Set max width to ensure it doesn't stretch too wide
    newSurvey.maxWidth = "1000px";

    // Add server-side validation to block navigation if sum does not match
    if (newSurvey.onServerValidateQuestions && typeof newSurvey.onServerValidateQuestions.add === "function") {
      newSurvey.onServerValidateQuestions.add((sender, options) => {
        const data = sender.data || {};
        const total = Number(data.total_guest_rooms) || 0;
        const studios = Number(data.no_of_studios) || 0;
        const oneBeds = Number(data.no_of_1_bedrooms) || 0;
        const twoBeds = Number(data.no_of_2_bedrooms) || 0;
        const threeBeds = Number(data.no_of_3_bedrooms) || 0;
        const sum = studios + oneBeds + twoBeds + threeBeds;

        const allFilled =
          data.total_guest_rooms !== undefined &&
          data.no_of_studios !== undefined &&
          data.no_of_1_bedrooms !== undefined &&
          data.no_of_2_bedrooms !== undefined &&
          data.no_of_3_bedrooms !== undefined;

        // Use a new errors object to avoid direct assignment to function parameter
        const newErrors = options.errors ? { ...options.errors } : {};

        if (allFilled && sum !== total) {
          const errorMsg =
            "The sum of all room types (studios, 1-bed, 2-bed, 3-bed) must equal the total number of guest rooms.";
          ["total_guest_rooms", "no_of_studios", "no_of_1_bedrooms", "no_of_2_bedrooms", "no_of_3_bedrooms"].forEach(
            (name) => {
              newErrors[name] = errorMsg;
            }
          );
        }

        // --- Floor percentage validation ---
        const floorFields = [
          "rooms_floor_percentage",
          "common_areas_floor_percentage",
          "restaurants_floor_percentage",
          "spas_floor_percentage",
          "back_offices_floor_percentage",
          "other_areas_floor_percentage",
        ];
        const floorValues = floorFields.map((f) => Number(data[f]));
        const allFloorFilled = floorValues.every((v) => !Number.isNaN(v));
        const floorSum = floorValues.reduce((a, b) => a + b, 0);
        if (allFloorFilled && floorSum !== 100) {
          const errorMsg = "The sum of all floor percentage fields must equal 100.";
          floorFields.forEach((name) => {
            newErrors[name] = errorMsg;
          });
        }
        // --- End floor percentage validation ---

        // eslint-disable-next-line no-param-reassign
        options.errors = newErrors;
        options.complete();
      });
    }

    // Add handler to clear custom errors when navigating between pages
    if (newSurvey.onCurrentPageChanged && typeof newSurvey.onCurrentPageChanged.add === "function") {
      newSurvey.onCurrentPageChanged.add((sender) => {
        ["total_guest_rooms", "no_of_studios", "no_of_1_bedrooms", "no_of_2_bedrooms", "no_of_3_bedrooms"].forEach(
          (name) => {
            const q = sender.getQuestionByName(name);
            if (q) q.clearErrors();
          }
        );
      });
    }

    setSurvey(newSurvey);
  }, [isPublicRoute, handlePublicRouteSubmission, showEmailVerification, showRestoreDataModal]);

  const handleExcelDataConfirm = (extractedData: any) => {
    if (surveyData) {
      // Create a copy of the survey data
      const updatedData = { ...surveyData };

      // Check if this is a reuploaded file
      const isReuploadedFile = extractedData.isReuploaded === true;

      // For reuploaded files, completely clear any existing Excel data
      if (isReuploadedFile) {
        // Clean up any data that might have been extracted from the invalid file
        if (updatedData.extractedExcelData) {
          delete updatedData.extractedExcelData;
        }

        if (updatedData.originalExcelData) {
          delete updatedData.originalExcelData;
        }

        if (updatedData.consumptionFileURL) {
          delete updatedData.consumptionFileURL;
        }
      }

      // Now add the new extracted data to the survey data - ensure no duplicates
      if (Array.isArray(extractedData.extractedExcelData)) {
        // Create a Map to deduplicate by month+year+data_type combo
        const uniqueRecordsMap = new Map();

        extractedData.extractedExcelData.forEach((record: any) => {
          // Create a unique key based on month, year, data_type, and consumption type
          const month = record.month || "";
          const year = record.year || "";
          const dataType = record.data_type || "";

          // Determine the consumption type from the record
          let consumptionType = "";
          if (record.electricity_consumption) consumptionType = "electricity";
          else if (record.water_consumption) consumptionType = "water";
          else if (record.gas_consumption) consumptionType = "gas";
          else if (record.waste_consumption) consumptionType = "waste";

          // Create a more specific unique key
          const uniqueKey = `${month}-${year}-${dataType}-${consumptionType}`;

          // Only keep the most recent entry for each unique key
          uniqueRecordsMap.set(uniqueKey, record);
        });

        // Convert map values back to array
        const deduplicatedRecords = Array.from(uniqueRecordsMap.values());

        // Use the deduplicated data
        updatedData.extractedExcelData = deduplicatedRecords;
      } else {
        // If not an array, use as-is
        updatedData.extractedExcelData = extractedData.extractedExcelData;
      }

      // Store the original Excel data structure for display purposes
      updatedData.originalExcelData = extractedData.originalData;

      // If the extracted data includes a consumptionFileURL (S3 upload link),
      // add it to the survey data for processing
      if (extractedData.consumptionFileURL) {
        updatedData.consumptionFileURL = extractedData.consumptionFileURL;
      }

      // Set the combined data for processing
      setCombinedData(updatedData);

      // Hide the Excel data extractor and show the survey processor
      setShowExcelExtractor(false);
      setShowSurveyProcessor(true);

      const successMessage = isReuploadedFile
        ? "Reuploaded Excel file processed successfully"
        : "Excel data extracted and added to the survey submission";

      message.success(successMessage);
    } else {
      message.error("No survey data available");
    }
  };

  const handleExcelDataCancel = () => {
    // Hide the Excel data extractor
    setShowExcelExtractor(false);

    // Show a message that the Excel data extraction was skipped
    message.info("Excel data extraction skipped");

    // Still proceed with survey processing using just the survey data
    setCombinedData(surveyData);
    setShowSurveyProcessor(true);
  };

  // New handler for Excel validation errors
  const handleExcelError = (error: string) => {
    setExcelError(error);
    setShowReuploadModal(true);
    setShowExcelExtractor(false);
  };

  // Handle file reupload
  const handleReupload = (fileData: {
    name: string;
    type: string;
    content: string;
    occupancy_data?: boolean;
    isReuploaded?: boolean;
    consumptionFileURL?: string;
    originalData?: any;
    jsonData?: any;
    s3Upload?: any;
  }) => {
    if (surveyData) {
      // Create a fresh survey data object without any Excel processing results
      // Instead of using delete, we'll create a new object with only non-Excel properties
      const cleanSurveyData: any = {
        // Copy non-Excel-related properties only
        ...surveyData,
        // Initialize with an empty array to avoid type errors and override any Excel data
        location_consumption: [],
        // Remove any Excel-related properties
        extractedExcelData: undefined,
        originalExcelData: undefined,
        consumptionFileURL: undefined,
      };

      // Ensure the file data is explicitly marked as reuploaded
      const updatedFileData = {
        ...fileData,
        isReuploaded: true, // Explicitly mark this as a reuploaded file
      };

      // Create a fresh location_consumption array with only the new file
      cleanSurveyData.location_consumption = [updatedFileData];

      // Update the survey data state with the clean version
      setSurveyData(cleanSurveyData);

      // Reset combinedData to avoid carrying over any previously processed Excel data
      setCombinedData(null);

      // Close the reupload modal and show the Excel extractor again
      setShowReuploadModal(false);
      setShowExcelExtractor(true);

      message.info("New Excel file uploaded. Validating...");
    }
  };

  // Cancel reupload and proceed without Excel data
  const handleReuploadCancel = () => {
    setShowReuploadModal(false);
    message.info("Excel data extraction skipped");

    // Proceed with survey processing using just the survey data without Excel data
    setCombinedData(surveyData);
    setShowSurveyProcessor(true);
  };

  // Handle completion of survey processing
  const handleProcessingComplete = (result: { locationId: string; userId: string }) => {
    message.success(`Successfully created location (${result.locationId}) and user (${result.userId})`);

    // Reset the state to allow for a new survey
    setShowSurveyProcessor(false);
    setSurveyData(null);
    setCombinedData(null);
    setExcelError(null);

    // Show redirecting state
    setIsRedirecting(true);

    // Use a timeout to give the success message time to appear
    // and ensure React finishes any pending updates
    setTimeout(() => {
      // For Project Coordinators, do NOT redirect after onboarding
      if (!roleAndPermission.isPC()) {
        // For Project Coordinators, redirect with a full page refresh to reset any cached state
        // (this block now only applies to non-PCs)
        if (roleAndPermission.isPC()) {
          window.location.href = "/locations";
        } else {
          history.push("/locations");
        }
      } else {
        // For PCs, reset redirecting state so the UI returns to normal
        setIsRedirecting(false);
      }
      // Always clear lastUsedEmail after onboarding
      localStorage.removeItem("lastUsedEmail");
      // If PC, do nothing (stay on page)
    }, 1500);
  };

  // Map survey data to the format expected by the processor
  const mapSurveyDataToProcessorFormat = (data: any) => {
    if (!data) return {};

    // Filter duplicate records in extractedExcelData if it exists
    let uniqueExcelData = null;
    if (data.extractedExcelData && Array.isArray(data.extractedExcelData) && data.extractedExcelData.length > 0) {
      // Create a Map to track unique record keys
      const uniqueMap = new Map();

      // Process each record to create unique keys based on month/year/data_type
      data.extractedExcelData.forEach((record: any) => {
        const month = record.month || "";
        const year = record.year || "";
        const dataType = record.data_type || "";
        // Also include consumption type in key to avoid different measurement duplicates
        // This handles the case where we have water, electricity, etc. for the same date
        let consumptionType = "";
        if (record.electricity_consumption) consumptionType = "electricity";
        else if (record.water_consumption) consumptionType = "water";
        else if (record.gas_consumption) consumptionType = "gas";
        else if (record.waste_consumption) consumptionType = "waste";

        const uniqueKey = `${month}-${year}-${dataType}-${consumptionType}`;
        uniqueMap.set(uniqueKey, record);
      });

      // Convert map back to array
      uniqueExcelData = Array.from(uniqueMap.values());
    }

    // Map the survey fields to the expected format
    const mappedData = {
      // User information
      first_name: data.name?.text1 || "",
      last_name: data.name?.text2 || "",
      company_role: data.company_role || "",
      phone_number: data.phone_number || "",
      email: data.email || "",

      // Location information
      location_name: data.location_name || "",
      organization_name: data.organization_name || data.company_name || data.location_name || "", // Fallback to location name if org name not specified

      // Address information
      address_1: data.address_1 || "",
      address_2: data.address_2 || "",
      city: data.city || "",
      state: data.state || "",
      zip_code: data.zip_code || "",
      country: data.country || "",

      // Property information
      total_square_footage: data.total_square_footage || "",
      hospitality_type: data.hospitality_type || "",
      total_guest_rooms: data.total_guest_rooms || "",
      no_of_studios: data.no_of_studios || "",
      no_of_1_bedrooms: data.no_of_1_bedrooms || "",
      no_of_2_bedrooms: data.no_of_2_bedrooms || "",
      no_of_3_bedrooms: data.no_of_3_bedrooms || "",
      rooms_floor_percentage: data.rooms_floor_percentage || "",
      common_areas_floor_percentage: data.common_areas_floor_percentage || "",
      restaurants_floor_percentage: data.restaurants_floor_percentage || "",
      spas_floor_percentage: data.spas_floor_percentage || "",
      back_offices_floor_percentage: data.back_offices_floor_percentage || "",
      other_areas_floor_percentage: data.other_areas_floor_percentage || "",
      hvac_system_type: Array.isArray(data.hvac_system_type)
        ? data.hvac_system_type.join(", ")
        : data.hvac_system_type || "",
      energy_initiatives: data.energy_initiatives || "",
      has_energy_initiatives: data.has_energy_initiatives || false,

      // Billing periods
      electricity_billing_period_start: data.electricity_billing_period_start || "",
      electricity_billing_period_end: data.electricity_billing_period_end || "",
      water_billing_period_start: data.water_billing_period_start || "",
      water_billing_period_end: data.water_billing_period_end || "",
      gas_billing_period_start: data.gas_billing_period_start || "",
      gas_billing_period_end: data.gas_billing_period_end || "",
      waste_billing_period_start: data.waste_billing_period_start || "",
      waste_billing_period_end: data.waste_billing_period_end || "",

      // Include extracted Excel data if available - using deduplicated data
      extractedExcelData: uniqueExcelData || data.extractedExcelData || null,
      consumptionFileURL: data.consumptionFileURL || null,
      currency: data.currency || "",
      hvac_consumption_percentage: data.hvac_consumption_percentage || "",
      // Include original Excel data for display
      originalExcelData: data.originalExcelData || null,
      location_consumption: data.location_consumption || [],

      // Add missing fields for processor
      laundry_processed: data.laundry_processed || "",
      has_solar_energy_system_installed: data.has_solar_energy_system_installed,
      solar_energy_percentage: data.solar_energy_percentage || "",
      solar_energy_installation_date: data.solar_energy_installation_date || "",
      energy_tariff: data.energy_tariff || "",
      other_renewable_energy_consumption_percentage: data.other_renewable_energy_consumption_percentage || "",
      has_major_bms_energy_equipment_change: data.has_major_bms_energy_equipment_change,
      // Ensure these fields are passed through with their original keys and values
      forecasted_occupancy_rate: data.forecasted_occupancy_rate,
      has_keycard_system: data.has_keycard_system,

      // Gas fields
      gas_type: data.gas_type || "",
      gas_usage: (() => {
        // If gas_usage is an array, check for 'other' and include the custom value
        if (Array.isArray(data.gas_usage)) {
          const otherKey = Object.keys(data).find(
            (k) => k.startsWith("gas_usage") && k.toLowerCase().includes("other")
          );
          const otherValue = otherKey ? data[otherKey] : null;
          // If 'other' is selected and a value is provided, include it
          if (data.gas_usage.includes("other") && otherValue) {
            return [...data.gas_usage.filter((v: string) => v !== "other"), otherValue];
          }
          return data.gas_usage;
        }
        return data.gas_usage || [];
      })(),

      // Submeter readings
      sub_metered_chiller_reading1: data.sub_metered_chiller_reading1 || "",
      sub_metered_chiller_reading2: data.sub_metered_chiller_reading2 || "",
      sub_metered_chiller_reading3: data.sub_metered_chiller_reading3 || "",
      sub_metered_chiller_reading4: data.sub_metered_chiller_reading4 || "",
      sub_metered_chiller_reading5: data.sub_metered_chiller_reading5 || "",
    };

    return mappedData;
  };

  // Don't return null if we have stored data to restore
  if (!survey && !showExcelExtractor && !showSurveyProcessor && !showReuploadModal && !showRestoreDataModal) {
    // Force check stored data before returning null
    setTimeout(() => checkStoredData(), 0);

    // Direct check for stored data - attempt immediate display of restore modal
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        if (parsedData && parsedData.data && Object.keys(parsedData.data).length > 0) {
          // Update state to open the modal on next render cycle
          setTimeout(() => {
            setStoredData(parsedData);
            setShowRestoreDataModal(true);
          }, 0);
        }
      }
    } catch (error) {
      // Silently fail on error - no debug logs needed
    }

    return <div>Loading SmartBEA...</div>;
  }

  // Handle return to login
  const handleReturnToLogin = () => {
    // Set email verification as completed
    localStorage.setItem("emailVerificationCompleted", "true");

    // Hide email verification screen
    setShowEmailVerification(false);

    // If already authenticated, trigger the verification completed effect
    if (isAuthenticated) {
      const storedEmail = localStorage.getItem("lastUsedEmail");
      if (storedEmail) {
        getSurveyDataByEmail(storedEmail)
          .then((data) => {
            if (data && data.surveyData) {
              // Set the survey data
              setSurveyData(data.surveyData);
              setShowExcelExtractor(true);
              setShowSurveyProcessor(false);
            }
          })
          .catch((error) => {
            logger.error("Error retrieving survey data on return to login:", error);
          });
      }
    } else {
      // If not authenticated, redirect to login
      loginWithRedirect({
        redirectUri: window.location.origin + window.location.pathname,
      });
    }
  };

  // Loading overlay
  if (isLoading || isRedirecting || smartBEAStatusLoading) {
    return (
      <div className="smart-bea-wrapper">
        <div className="smart-bea-container" style={{ maxWidth: "1000px", textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
          <p style={{ marginTop: "20px" }}>
            {isRedirecting ? "Redirecting to Locations Dashboard..." : "Processing your information..."}
          </p>
        </div>
      </div>
    );
  }

  // Show restriction screen if user is already onboarded with locations
  // (this will only be shown for public route access, as the main route redirect happens in useEffect)
  if (isRestricted && isPublicRoute()) {
    return <RestrictedSmartBEA />;
  }

  // Show email verification screen
  if (showEmailVerification) {
    return (
      <div className="smart-bea-wrapper">
        <div className="smart-bea-container" style={{ maxWidth: "1000px" }}>
          <EmailVerification email={userEmail} onReturnToLogin={handleReturnToLogin} />
        </div>
      </div>
    );
  }

  if (showExcelExtractor && surveyData) {
    return (
      <div className="smart-bea-wrapper">
        <div className="smart-bea-container" style={{ maxWidth: "1000px" }}>
          <ExcelDataExtractor
            jsonData={surveyData}
            onConfirm={handleExcelDataConfirm}
            onCancel={handleExcelDataCancel}
            onError={handleExcelError}
          />
        </div>
      </div>
    );
  }

  if (showSurveyProcessor && (surveyData || combinedData)) {
    const processData = combinedData || surveyData;
    const mappedData = mapSurveyDataToProcessorFormat(processData);

    return (
      <div className="smart-bea-wrapper">
        <div className="smart-bea-container" style={{ maxWidth: "1000px" }}>
          <SurveyResponseProcessor surveyResponse={mappedData} onComplete={handleProcessingComplete} />
        </div>
      </div>
    );
  }

  return (
    <div className="smart-bea-wrapper">
      <div className="smart-bea-container">
        <Survey model={survey} />

        {/* Reupload Modal */}
        <ExcelReuploadModal
          visible={showReuploadModal}
          errorMessage={excelError}
          onReupload={handleReupload}
          onCancel={handleReuploadCancel}
        />

        {/* Restore Data Modal */}
        <Modal
          title="Resume Your Previous Form"
          open={showRestoreDataModal}
          onCancel={handleDiscardStoredData}
          footer={[
            <Button key="discard" onClick={handleDiscardStoredData}>
              Start Fresh
            </Button>,
            <Button key="restore" type="primary" onClick={handleRestoreData}>
              Restore Previous Data
            </Button>,
          ]}
        >
          <p>
            We found your previously entered data. Would you like to continue where you left off or start with a fresh
            form?
          </p>
        </Modal>
      </div>
    </div>
  );
};

export default SmartBEA;
